import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import {
  useGetProductsByClass,
  useGetProductsByTag,
  useGetWeeklyHighlights
} from '@cannect/services/resources/products/hooks'
import { pickAffinityTag } from '@cannect/utils/productAffinityTag'
import { useCallback, useEffect, useState } from 'react'

// A garantia de que só voltam produtos vendáveis é o `only_sellable=true` enviado ao backend
// (filtro no SQL, cobre inclusive o caminho com search engine/Typesense ativo, que antes podia
// intercalar itens inaptos entre os primeiros resultados). O filtro client `sellable === false`
// em `availableProducts` fica como fallback de defesa em profundidade (ex.: cache antigo,
// resposta de uma API ainda sem o flag). O over-fetch abaixo continua útil porque produtos já
// no carrinho ou já adicionados via handleAddToCart são descartados no client, e isso pode
// reduzir a lista abaixo do `limit` pedido.
const SUGGESTED_FETCH_MULTIPLIER = 2
// 8 coincide com o default dos requests em `requests.ts` (getWeeklyHighlightsPublic,
// getProductsByClassPublic, getProductsByTagPublic).
const SUGGESTED_FETCH_MIN = 8

interface Product {
  id: number
  name?: string
  translated_name?: string
  price: number
  promotional_price?: number
  real_price?: number
  original_price?: number
  media?: Array<{ path: string; format: string }>
  brand_id?: number
  type?: string
  class_id?: number
  supplier_id?: number
  stock?: number
  currency?: string
  flavor_id?: number
  flavor?: any
  flavors?: Array<{
    id: number
    flavor: string
    mainProduct?: number
    balance?: number
    total?: number
  }>
  promotion?: Array<{
    regular_price?: any
    promotional_price?: any
    discount_value?: any
    discount_type?: any
  }>
  sellable?: boolean
}

interface UseSuggestedProductsOptions {
  limit?: number
  excludeProductIds?: number[]
  filterByCartItems?: boolean
  classId?: number
}

interface UseSuggestedProductsReturn {
  products: Product[]
  isLoading: boolean
  error: any
  addedProducts: Set<number>
  addingProduct: number | null
  handleAddToCart: (product: Product, onAddToCart?: (product: Product) => void) => Promise<void>
  isProductAdded: (productId: number) => boolean
  resetAddedProducts: () => void
}

export function useSuggestedProducts(options: UseSuggestedProductsOptions = {}): UseSuggestedProductsReturn {
  const { limit = 5, excludeProductIds = [], filterByCartItems = false, classId } = options

  const { isUpdate, lastOrderCreated } = usePrepareCart()

  const firstCartItemProduct = filterByCartItems ? lastOrderCreated?.items?.[0]?.product : undefined
  const cartAffinityTag = pickAffinityTag(firstCartItemProduct?.shopifyTags)
  const cartDerivedClassId = firstCartItemProduct?.class_id

  const effectiveTag = !classId ? cartAffinityTag : undefined
  const effectiveClassId = classId ?? (!effectiveTag ? cartDerivedClassId : undefined)

  const fetchLimit = Math.max(limit * SUGGESTED_FETCH_MULTIPLIER, SUGGESTED_FETCH_MIN)

  const weeklyHighlightsQuery = useGetWeeklyHighlights(fetchLimit, !effectiveTag && !effectiveClassId, true)
  const productsByTagQuery = useGetProductsByTag(effectiveTag ?? '', fetchLimit, !!effectiveTag, true)
  const productsByClassQuery = useGetProductsByClass(
    effectiveClassId ?? 0,
    fetchLimit,
    !effectiveTag && !!effectiveClassId,
    true
  )

  let activeQuery = weeklyHighlightsQuery
  if (effectiveTag) {
    activeQuery = productsByTagQuery
  } else if (effectiveClassId) {
    activeQuery = productsByClassQuery
  }

  const { data: productsSource, isLoading, error } = activeQuery
  const [addedProducts, setAddedProducts] = useState<Set<number>>(new Set())
  const [addingProduct, setAddingProduct] = useState<number | null>(null)

  useEffect(() => {
    if (!isUpdate && addingProduct) {
      setAddingProduct(null)
    }
  }, [isUpdate, addingProduct])

  const cartProductIds = filterByCartItems ? (lastOrderCreated?.items || []).map((item: any) => item.product.id) : []

  const availableProducts =
    productsSource?.products
      ?.filter((product: Product) => {
        if (product.sellable === false) return false

        if (addedProducts.has(product.id)) return false

        if (excludeProductIds.includes(product.id)) return false

        if (filterByCartItems && cartProductIds.includes(product.id)) return false

        return true
      })
      ?.slice(0, limit) || []

  const handleAddToCart = async (product: Product, onAddToCart?: (product: Product) => void) => {
    try {
      setAddingProduct(product.id)

      if (onAddToCart) {
        await onAddToCart(product)
      }

      setAddedProducts((prev) => new Set([...prev, product.id]))
    } catch (error) {
      console.error('Erro ao adicionar produto:', error)
      setAddingProduct(null)
    }
  }

  const isProductAdded = (productId: number): boolean => {
    return addedProducts.has(productId)
  }

  const resetAddedProducts = () => {
    setAddedProducts(new Set())
  }

  return {
    products: availableProducts,
    isLoading,
    error,
    addedProducts,
    addingProduct,
    handleAddToCart,
    isProductAdded,
    resetAddedProducts
  }
}
